import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Megaphone, Target, Mic2, CalendarDays, Route, Sparkles } from "lucide-react";
import { ElephantLogo } from "@/components/ElephantLogo";
import { useLang, STR, loc } from "@/i18n/translations";
import { ArtistPhoto, ArtistOverlay } from "@/components/ArtistPhoto";
import { useCart } from "@/context/CartContext";
import { SocialLinks, AGENCY_SOCIALS } from "@/components/SocialIcons";
import api from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1499364615650-ec38552f4f34?crop=entropy&cs=srgb&fm=jpg&q=85";
const SERVICE_ICONS = { digital: Megaphone, ads: Target, booking: Mic2, events: CalendarDays, tours: Route };

export default function Home() {
  const { t, lang } = useLang();
  const { add } = useCart();
  const [artists, setArtists] = useState([]);
  const [events, setEvents] = useState([]);
  const [products, setProducts] = useState([]);

  useEffect(() => {
    api.get("/artists").then((r) => setArtists(r.data.slice(0, 4))).catch(() => {});
    api.get("/events").then((r) => setEvents(r.data.filter((e) => e.status === "upcoming").slice(0, 3))).catch(() => {});
    api.get("/products").then((r) => setProducts(r.data.slice(0, 3))).catch(() => {});
  }, []);

  return (
    <div>
      {/* HERO */}
      <section className="relative min-h-screen flex items-center overflow-hidden grain">
        <div className="absolute inset-0">
          <img src={HERO} alt="" className="w-full h-full object-cover opacity-40" />
          <div className="absolute inset-0 bg-gradient-to-b from-obsidian/80 via-obsidian/70 to-obsidian" />
          <div className="absolute inset-0 bg-gradient-to-r from-obsidian via-obsidian/40 to-transparent" />
        </div>

        <div className="absolute right-4 lg:right-24 top-1/3 opacity-20 animate-glow pointer-events-none">
          <ElephantLogo className="w-64 h-64 lg:w-96 lg:h-96" />
        </div>

        <div className="relative max-w-7xl mx-auto px-5 lg:px-8 w-full pt-24">
          <motion.p initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="eyebrow mb-6">
            {t(STR.hero.eyebrow)}
          </motion.p>
          <motion.h1
            initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.1 }}
            className="font-serif text-4xl sm:text-6xl lg:text-7xl font-bold leading-[1.05] max-w-4xl"
          >
            {t(STR.hero.title)}
          </motion.h1>
          <motion.p
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.25 }}
            className="mt-7 text-base sm:text-lg text-cream/70 max-w-xl leading-relaxed"
          >
            {t(STR.hero.subtitle)}
          </motion.p>
          <motion.p
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.4 }}
            className="mt-6 font-serif italic text-2xl gold-gradient-text"
          >
            « {t(STR.common.slogan)} »
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.5 }}
            className="mt-10 flex flex-col sm:flex-row gap-4"
          >
            <Link to="/booking" data-testid="hero-booking-btn" className="btn-gold rounded-full px-8 py-4 flex items-center justify-center gap-2">
              {t(STR.common.requestBooking)} <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/services" data-testid="hero-project-btn" className="btn-outline-gold rounded-full px-8 py-4 flex items-center justify-center gap-2">
              {t(STR.common.developProject)}
            </Link>
          </motion.div>
        </div>
      </section>

      {/* ABOUT */}
      <section className="py-24 lg:py-32 max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-2 gap-14 items-center">
        <div>
          <p className="eyebrow mb-5">{t(STR.home.aboutEyebrow)}</p>
          <h2 className="font-serif text-3xl lg:text-5xl font-semibold leading-tight">{t(STR.home.aboutTitle)}</h2>
          <div className="gold-line w-24 my-8" />
          <p className="text-cream/70 leading-relaxed text-lg">{t(STR.home.aboutText)}</p>
          <Link to="/a-propos" className="inline-flex items-center gap-2 mt-8 text-gold hover-underline" data-testid="home-about-link">
            {t(STR.common.learnMore)} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="relative">
          <div className="card-dark rounded-2xl p-10 flex items-center justify-center aspect-square">
            <ElephantLogo className="w-56 h-56" />
          </div>
          <div className="absolute -bottom-6 -left-6 bg-gold text-obsidian rounded-xl px-6 py-4 shadow-xl">
            <Sparkles className="w-5 h-5 mb-1" />
            <p className="font-serif text-xl font-bold">{t({ fr: "Afro-contemporain", en: "Afro-contemporary" })}</p>
          </div>
        </div>
      </section>

      {/* SERVICES */}
      <section className="py-24 bg-[#0c0c0e] border-y border-gold/10">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex flex-col lg:flex-row justify-between lg:items-end gap-6 mb-14">
            <div>
              <p className="eyebrow mb-4">{t(STR.services.eyebrow)}</p>
              <h2 className="font-serif text-3xl lg:text-5xl font-semibold">{t(STR.home.ctaTitle) && t(STR.services.title)}</h2>
            </div>
            <Link to="/services" className="text-gold hover-underline flex items-center gap-2" data-testid="home-services-link">
              {t(STR.common.viewAll)} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {STR.services.items.map((s, i) => {
              const Icon = SERVICE_ICONS[s.key];
              return (
                <motion.div
                  key={s.key}
                  initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: i * 0.08 }}
                  className="card-dark rounded-xl p-8"
                  data-testid={`home-service-${s.key}`}
                >
                  <div className="w-12 h-12 rounded-lg bg-gold/10 border border-gold/30 flex items-center justify-center mb-6">
                    <Icon className="w-5 h-5 text-gold" />
                  </div>
                  <h3 className="font-serif text-2xl mb-3">{lang === "fr" ? s.fr : s.en}</h3>
                  <p className="text-cream/60 text-sm leading-relaxed">{lang === "fr" ? s.dfr : s.den}</p>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* ARTISTS */}
      <section className="py-24 lg:py-32 max-w-7xl mx-auto px-5 lg:px-8">
        <div className="flex flex-col lg:flex-row justify-between lg:items-end gap-6 mb-14">
          <h2 className="font-serif text-3xl lg:text-5xl font-semibold">{t(STR.home.artistsTitle)}</h2>
          <Link to="/artistes" className="text-gold hover-underline flex items-center gap-2" data-testid="home-artists-link">
            {t(STR.common.viewAll)} <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-5">
          {artists.map((a, i) => (
            <Link
              key={a.id} to={`/artistes/${a.slug}`} data-testid={`home-artist-${a.slug}`}
              className="group relative rounded-xl overflow-hidden aspect-[3/4] border border-gold/15"
            >
              <ArtistPhoto src={a.image} alt={a.name} position={a.image_position} sizes="(max-width: 1024px) 50vw, 25vw" className="transition-transform duration-700 group-hover:scale-110" />
              <ArtistOverlay />
              <div className="absolute bottom-0 p-4 lg:p-5 text-shadow-strong">
                <h3 className="font-serif text-2xl lg:text-3xl font-semibold text-white leading-tight" data-testid={`home-artist-name-${a.slug}`}>{a.name}</h3>
                <p className="mt-1.5 text-xs lg:text-sm text-gold-light font-semibold leading-snug" data-testid={`home-artist-genre-${a.slug}`}>{loc(a, "genre", lang)}</p>
              </div>
            </Link>
          ))}
        </div>
      </section>

      {/* FOLLOW US */}
      <section className="max-w-7xl mx-auto px-5 lg:px-8 pb-24" data-testid="home-follow-us">
        <div className="card-dark rounded-2xl p-8 lg:p-12 border border-gold/20 grid lg:grid-cols-[1fr_auto] gap-8 items-center grain relative overflow-hidden">
          <div>
            <p className="eyebrow mb-3">{t({ fr: "Suivez-nous", en: "Follow us" })}</p>
            <h2 className="font-serif text-3xl lg:text-4xl mb-3">{t({ fr: "Sily Agency sur les réseaux", en: "Sily Agency on social media" })}</h2>
            <p className="text-cream/70 max-w-xl">{t({ fr: "Coulisses, annonces de concerts, nouveautés de nos artistes : rejoignez la communauté.", en: "Behind the scenes, concert announcements and artist news: join the community." })}</p>
          </div>
          <SocialLinks links={AGENCY_SOCIALS} owner="Sily Agency" prefix="home-social" showLabel
            itemClass="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-gold/40 text-cream hover:bg-gold hover:text-obsidian transition-colors text-sm" />
        </div>
      </section>

      {/* EVENTS */}
      {events.length > 0 && (
        <section className="py-24 bg-[#0c0c0e] border-y border-gold/10">
          <div className="max-w-7xl mx-auto px-5 lg:px-8">
            <div className="flex flex-col lg:flex-row justify-between lg:items-end gap-6 mb-14">
              <h2 className="font-serif text-3xl lg:text-5xl font-semibold">{t(STR.home.eventsTitle)}</h2>
              <Link to="/evenements" className="text-gold hover-underline flex items-center gap-2" data-testid="home-events-link">
                {t(STR.common.viewAll)} <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
            <div className="space-y-4">
              {events.map((e) => (
                <div key={e.id} className="card-dark rounded-xl p-6 flex flex-col sm:flex-row sm:items-center gap-4 justify-between" data-testid={`home-event-${e.id}`}>
                  <div className="flex items-center gap-6">
                    <div className="text-center min-w-[70px]">
                      <p className="text-gold font-serif text-3xl">{e.date?.split("-")[2]}</p>
                      <p className="text-xs uppercase tracking-widest text-cream/50">{e.date?.slice(0, 7)}</p>
                    </div>
                    <div>
                      {e.is_tour && <span className="text-[10px] uppercase tracking-widest bg-crimson-accent/20 text-crimson-accent border border-crimson-accent/40 px-2 py-0.5 rounded">Sily Tour</span>}
                      <h3 className="font-serif text-2xl mt-1">{loc(e, "title", lang)}</h3>
                      <p className="text-cream/60 text-sm">{loc(e, "artist", lang)} · {loc(e, "city", lang)} · {loc(e, "venue", lang)}</p>
                    </div>
                  </div>
                  <Link to="/booking" className="btn-outline-gold rounded-full px-5 py-2 text-sm text-center">{t(STR.common.tickets)}</Link>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* SHOP */}
      {products.length > 0 && (
        <section className="py-24 lg:py-32 max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex flex-col lg:flex-row justify-between lg:items-end gap-6 mb-4">
            <h2 className="font-serif text-3xl lg:text-5xl font-semibold">{t(STR.home.shopTitle)}</h2>
            <Link to="/boutique" className="text-gold hover-underline flex items-center gap-2" data-testid="home-shop-link">
              {t(STR.common.viewAll)} <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          <p className="text-cream/60 mb-12">{t(STR.home.shopText)}</p>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
            {products.map((p) => (
              <div key={p.id} className="card-dark rounded-xl overflow-hidden group" data-testid={`home-product-${p.id}`}>
                <div className="aspect-square overflow-hidden">
                  <img src={p.image} alt={loc(p, "name", lang)} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-5 flex justify-between items-center">
                  <div>
                    <h3 className="font-serif text-xl">{loc(p, "name", lang)}</h3>
                    <p className="text-gold">{p.price.toFixed(2)} €</p>
                  </div>
                  <button onClick={() => add(p)} data-testid={`home-add-${p.id}`} className="btn-gold rounded-full px-4 py-2 text-xs">{t(STR.common.addToCart)}</button>
                </div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* CTA */}
      <section className="py-28 relative overflow-hidden grain border-t border-gold/10">
        <div className="max-w-4xl mx-auto px-5 text-center relative">
          <ElephantLogo className="w-16 h-16 mx-auto mb-8" />
          <h2 className="font-serif text-4xl lg:text-6xl font-semibold leading-tight">{t(STR.home.ctaTitle)}</h2>
          <div className="mt-10 flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/booking" data-testid="cta-booking-btn" className="btn-gold rounded-full px-8 py-4">{t(STR.common.requestBooking)}</Link>
            <Link to="/contact" data-testid="cta-contact-btn" className="btn-outline-gold rounded-full px-8 py-4">{t(STR.common.developProject)}</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
